import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { getCurrentFolder } from '@alfresco/aca-shared/store';
import { from, Observable, of, ReplaySubject, Subject, zip } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  reduce,
  retry,
  switchMap,
  take,
} from 'rxjs/operators';

import { Node, NodeEntry, SiteEntry } from '@alfresco/js-api';
import { AppConfigService } from '@alfresco/adf-core';
import { ContentApiService } from '@alfresco/aca-shared';

import { NODE_CONSTANT } from '../model/alf-model';
import { AppStore } from '../store/states/app.state';
import { WkAlfrescoApiService } from './wk-alfresco-api.service';
import { CurrentFolderConfiguration } from '../types';
import {
  NodeProcessDialogComponent,
  NodeProcessDialogData,
} from '../dialogs/node-process/node-process.dialog';
import { SnackbarErrorAction } from '../store/actions/app.custom-actions';
import {
  AllowableOperationsEnum,
  ContentService,
  FileModel,
  PeopleContentService,
} from '@alfresco/adf-content-services';

@Injectable({
  providedIn: 'root',
})
export class NodeInfoService {
  uploadFolderId;
  private readonly RENDITION_CONTENT_URL_MAX_RETRIES = 10;
  private nodeId: string;
  private readonly currentNode$ = new ReplaySubject<NodeEntry>(1);
  private readonly siteFlag$ = new Subject<boolean>();

  private readonly contentType = ['application/json'];

  constructor(
    private readonly http: HttpClient,
    private readonly contentApiService: ContentApiService,
    private readonly contentService: ContentService,
    private readonly appConfigService: AppConfigService,
    private readonly api: WkAlfrescoApiService,
    private readonly store: Store<AppStore>,
    private readonly ecmUserService: PeopleContentService,
    private readonly dialogRef: MatDialog
  ) {}

  getNodeId() {
    return this.nodeId;
  }

  setNodeId(id: string) {
    this.nodeId = id;
  }

  getFolderId() {
    return this.uploadFolderId;
  }

  setFolderId(id: any) {
    this.uploadFolderId = id;
  }

  checkOut(id: string): Promise<object> {
    this.nodeId = id;

    const checkoutParams = {
      ids: [id],
    };

    return this.api.callContentClientCustomApi({
      path: `alfresco/service/wk/nodes/checkout`,
      httpMethod: 'POST',
      bodyParam: checkoutParams,
      contentTypes: this.contentType,
      accepts: this.contentType,
    });
  }

  checkIn(
    id: string | string[],
    data: { comment: string; version: any },
    location
  ): Promise<object> {
    const httpParams = {
      version: data.version,
      comment: data.comment || '',
    };

    httpParams['ids'] = location === 'edit' ? [id] : id;

    return this.api.callContentClientCustomApi({
      path: `alfresco/service/wk/nodes/checkin`,
      httpMethod: 'POST',
      bodyParam: httpParams,
      contentTypes: this.contentType,
      accepts: this.contentType,
    });
  }

  setCurrentNode(nodeEntry: NodeEntry) {
    if (nodeEntry === null) {
      this.currentNode$.next(nodeEntry);
      this.siteFlag$.next(false);
    } else {
      const node = nodeEntry.entry;
      if (
        node &&
        node.path.name &&
        node.path.name.includes(NODE_CONSTANT.SITES)
      ) {
        this.siteFlag$.next(true);
        this.currentNode$.next(nodeEntry);
      } else {
        this.siteFlag$.next(false);
      }
    }
  }

  getCurrentNode(): Observable<NodeEntry> {
    return this.currentNode$.asObservable();
  }

  getSiteFlag(): Observable<boolean> {
    return this.siteFlag$.asObservable();
  }

  getSiteFromNodePath(node: Node): Observable<SiteEntry> {
    let site;
    if (node.path && node.path.elements) {
      site = node.path.elements.find(
        (pathNode: Node) =>
          pathNode.nodeType === 'st:site' && pathNode.name !== 'Sites'
      );
    }
    return this.contentApiService.getSite(site.name);
  }

  getUpdatePermission(node: Node): boolean {
    return this.contentService.hasAllowableOperations(
      node,
      AllowableOperationsEnum.UPDATE
    );
  }

  getPdfDataAsBlob(url: string): Observable<Blob> {
    const creds = this.appConfigService.get<boolean>(
      'auth.withCredentials',
      false
    );
    const noCacheHeaders = new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    });
    return this.http
      .get(url, {
        headers: noCacheHeaders,
        withCredentials: creds,
        responseType: 'blob',
      })
      .pipe(
        retry(this.RENDITION_CONTENT_URL_MAX_RETRIES),
        catchError(() => of(new Blob()))
      );
  }

  getXMLContentFromUrl(url: string) {
    const creds = this.appConfigService.get<boolean>(
      'auth.withCredentials',
      false
    );
    const noCacheHeaders = new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    });
    return this.http.get(url, {
      headers: noCacheHeaders,
      withCredentials: creds,
      responseType: 'text',
    });
  }

  private getDuplicates(queryOptions: {
    fileSize: number;
    hash: string;
  }): Promise<any> {
    return this.api.callContentClientCustomApi({
      path: `alfresco/s/wk/nodes/assets/duplicates`,
      httpMethod: 'GET',
      queryParams: queryOptions,
      contentTypes: this.contentType,
      accepts: this.contentType,
    });
  }

  filterDuplicateFiles(files: FileModel[]): Observable<Array<any>> {
    return from(
      Promise.all(
        files.map((fileModel) =>
          fileModel.file
            .arrayBuffer()
            .then((buffer) => this.getCrypto().subtle.digest('SHA-256', buffer))
            .then((buffer) =>
              Array.from(new Uint8Array(buffer))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('')
            )
            .then((hash) => ({
              fileSize: fileModel.size,
              hash,
            }))
            .then((ele) => this.getDuplicates(ele))
            .then((val) =>
              typeof val === 'string'
                ? JSON.parse(val).duplicates
                : val.duplicates
            )
        )
      ).then((duplicateArrays: Array<Array<any>>) =>
        duplicateArrays.reduce((acc, curr) => acc.concat(curr))
      )
    );
  }

  getCrypto(): Crypto {
    return window.crypto;
  }

  getFolderConfiguration(
    paths: Array<string> = null
  ): Observable<CurrentFolderConfiguration | Array<any>> {
    const currentFolder$ = this.store.select(getCurrentFolder).pipe(
      switchMap((currentFolder) =>
        of({ currentFolder, selectedId: this.uploadFolderId })
      ),
      take(1)
    );
    if (!paths) {
      return currentFolder$;
    }
    console.log('paths:  ', paths);
    return of(...paths).pipe(
      concatMap((path) => zip(of(path), this.getNodeIdFromPath(path))),
      map((entry) => ({ [`${entry[0]}`]: entry[1].id })),
      reduce(
        (pathObject, currentPath) => ({ ...pathObject, ...currentPath }),
        {}
      ),
      switchMap((pathObject) => zip(of(pathObject), currentFolder$))
    );
  }

  getNodeIdFromPath(path: string) {
    const query = {
      query: {
        query: `PATH: '${path}'`,
        language: 'afts',
      },
    };
    return this.api.searchApi.search(query).then((data) => {
      console.log(query, data);
      return data.list.entries[0].entry;
    });
  }

  getParentId(parentFolderConfiguration: any, type: string) {
    if (parentFolderConfiguration.configuration.length > 0) {
      for (const obj of parentFolderConfiguration.configuration) {
        if (obj[type]) {
          return obj.id;
        }
      }
    } else if (parentFolderConfiguration.selectedId) {
      return parentFolderConfiguration.selectedId;
    }
    return parentFolderConfiguration.currentFolder.id;
  }

  getUsersFortrackChanges(users: string[]): Promise<any> {
    const usersInfo = users.map(async (id) => {
      const userDetail = await this.getUserDetail(id);
      const userObject = {
        id,
        name: userDetail.displayName,
      };
      if (userDetail.avatarId) {
        userObject['avatar'] = this.ecmUserService.getUserProfileImage(
          userDetail.avatarId
        );
      }
      return userObject;
    });
    return Promise.all(usersInfo);
  }

  async getUserDetail(userId: string): Promise<any> {
    const data = await this.api.callContentClientCustomApi({
      path: `alfresco/api/-default-/public/alfresco/versions/1/people/${userId}`,
      httpMethod: 'GET',
      contentTypes: this.contentType,
      accepts: this.contentType,
    });
    return data.entry;
  }

  saveXMLChanges(nodeId: string, content: string) {
    return from(
      this.api.callContentClientCustomApi({
        path: `alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/content?majorVersion=false`,
        httpMethod: 'PUT',
        bodyParam: content,
        contentTypes: ['application/octect-stream'],
        accepts: this.contentType,
      })
    );
  }

  startProcess(nodes: NodeEntry[]) {
    if (nodes) {
      this.dialogRef.open<
        NodeProcessDialogComponent,
        NodeProcessDialogData,
        void
      >(NodeProcessDialogComponent, {
        data: { nodes },
        width: '640px',
      });
    } else {
      this.store.dispatch(
        new SnackbarErrorAction('APP.MESSAGES.ERRORS.PERMISSION')
      );
    }
  }
}
